import { useEffect } from 'react';
import { generatePath, Redirect, Route, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { t } from 'ttag';

import { NAMESPACES } from '@constants';
import { publicMemberProfile } from '@urls';
import { useAppContext } from '@contexts/AppContext';
import { useIsClient } from '@utils/common';
import { getIsProductCanBePromoted } from '@utils/products';
import { ProductExpirationStatusType, ProductUserStatusType, ProductVisibleStatusType } from '@v5Models';
import { useDesktopOrWider } from '@utils/mediaQuery';
import { useViewedProducts } from '@hook/useViewedProducts';
import { useGotchaWebVitals } from '@hook/useGotchaWebVitals';
import { NavLayout } from '@layouts/NavLayout';
import { Alert } from '@ui/Alert';
import { Title } from '@ui/Title';
import { Button } from '@ui/Button';
import { ViewportLazy } from '@components/ViewportLazy';
import { ProductDetailsBreadcrumbsContainer } from '@containers/product/ProductDetailsBreadcrumbs';
import { ProductDetailsInfoContainer } from '@containers/product/ProductDetailsInfo';
import { ProductDetailsSellerInfoContainer } from '@containers/product/ProductDetailsSelelrInfo';
import { ProductDetailsSafePaymentHelpLink } from '@components/product/ProductDetailsSafePaymentHelpLink';
import { ProductDetailsPostsContainer } from '@containers/ProductDetailsPosts';
import { ProductDetailsPropertiesContainer } from '@containers/product/ProductDetailsProperties';
import { ProductDetailsSellerMethodsContainer } from '@containers/product/ProductDetailsSellerMethods';
import { ProductDetailsReviewsContainer } from '@containers/product/ProductDetailsReviews';
import { ProductDetailsViewedProducts } from '@components/product/ProductDetailsViewedProducts';
import { UserComplaintContainer } from '@containers/product/ProductDetailsUserComplaint';
import { ProductDetailsSocialsShareContainer } from '@containers/product/ProductDetailsSocialsShare';
import { ProductDetailsFeatureBannerContainer } from '@containers/ProductDetailsFeatureBanner';
import { ProductDetailsMetaContainer } from '@containers/product/ProductDetailsMeta';
import { ProductDetailsAds } from '@components/product/ProductDetailsAds';
import { ProductDetailsTopSellersProductsContainer } from '@containers/product/ProductDetailsTopSellersProducts';
import { ProductDetailsOwnerProductsContainer } from '@containers/product/ProductDetailsOwnerProducts';
import { ProductDetailsSimilarProductsContainer } from '@containers/product/ProductDetailsSimilarProducts';
import { ProductDetailsTopProductsContainer } from '@containers/product/ProductDetailsTopProducts';
import { ProductDetailsBottomBarContainer } from '@containers/product/ProductDetailsBottomBar';
import { ProductDetailsMessageFormContainer } from '@containers/product/ProductDetailsMessageForm';
import { ProductDetailsAnalyticsContainer } from '@containers/ProductDetailsAnalytics';
import { ProductDetailsAlsoSearchContainer } from '@containers/product/ProductDetailsAlsoSearch';
import { ProductDetailsStartSellingBanner } from '@components/product/ProductDetailsStartSellingBanner';
import { ProductDetailsPromotionServicesContainer } from '@containers/ProductDetailsPromotionServices';
import { ProductDetailsSellerSettingsContainer } from '@containers/product/ProductDetailsSellerSettings';

import { useProductDetailsPageV5Query } from './gql/graphql.v5.generated';
import {
  useProductDetailsPageCatalogQuery,
  useProductDetailsPageProductQuery,
  useProductDetailsPageViewerQuery,
} from './gql/graphql.v3.generated';

import s from './ProductDetailsPage.sss';

interface RouteParams {
  namespace: string,
  category: string,
  productId: string,
  slug: string,
}

export const ProductDetailsPage = () => {
  const params = useParams<RouteParams>();

  const { isBot } = useAppContext();
  const isClient = useIsClient();
  const isDesktop = useDesktopOrWider();

  const { addViewedProductId } = useViewedProducts();

  const productId = Number(params.productId);

  const { data: productDetailsViewerData } = useProductDetailsPageViewerQuery({
    context: {
      ignoreAuthError: true,
    },
    skip: isBot
  });

  const { data: productData } = useProductDetailsPageV5Query({
    variables: {
      productId,
    },
    context: { v5: true },
  });

  const { data: productDetailsData } = useProductDetailsPageProductQuery({
    variables: {
      productId,
      catalogSlug: params.category,
    },
    errorPolicy: "all"
  });

  const { data: catalogData } = useProductDetailsPageCatalogQuery({
    variables: {
      catalogSlug: params.category,
    },
  });

  useGotchaWebVitals({
    pageName: 'product_new',
  });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [productId]);

  useEffect(
    () => addViewedProductId(productId),
    [addViewedProductId, productId],
  );

  const isProductSold = productData?.product?.statusTitle === ProductVisibleStatusType.Sold;
  const isProductDeclined = productData?.product?.statusTitle === ProductVisibleStatusType.Declined;
  const isProductModerationRequired = productData?.product?.statusTitle === ProductVisibleStatusType.ModerationRequired;
  const isProductHidden = productData?.product?.userStatusTitle === ProductUserStatusType.Hidden;
  const isProductInactive = isProductSold || isProductHidden;

  let alertMessage = '';

  if (isProductSold) {
    alertMessage = t`Цей товар вже проданий, але є багато схожих`;
  } else if (isProductHidden) {
    alertMessage = t`Цей товар наразi неактивний, але є багато схожих`;
  }

  if (productData?.product && productDetailsData?.productDetails) {
    const product = productData.product;
    const catalog = productData.product.catalog;
    // not p.owner.is_active ??
    // or p.visibility == p.VISIBILITY_DELETED
    // or (p.status != Product.STATUS_VERIFIED and p.owner != request.user)
    const isProductRestrictVisible = (
      isProductSold
      || isProductDeclined
      || isProductModerationRequired
    );
    const productNamespace = NAMESPACES.find((namespace) => namespace.id === catalog?.namespace?.value);
    const productCatalogPath = productNamespace ? `/${productNamespace.slug}/${catalog.slug}` : '/clothes';

    // If product does not exist
    if (!product.id) {
      return (
        <Redirect to={productCatalogPath} />
      );
    }

    // p.is_sold or p.is_deactivated
    if (isProductSold || isProductHidden) {
      if (product.expirationStatus === ProductExpirationStatusType.Expired_410) {
        return (
          <Route
            render={({ staticContext }) => {
              if (staticContext) {
                staticContext.statusCode = 410;
              }
              return (
                <NavLayout className={s.root}>
                  <Title
                    className={s.notFoundTitle}
                    type='h1'
                    bold={false}
                  >
                    {t`Сторінку, яку ви шукали`}
                    &mdash;
                    {t`не знайдено`}
                  </Title>

                  <Button
                    className={s.notFoundButton}
                    to={productCatalogPath}
                  >
                    {t`Подивитися інші речі з цієї категорії`}
                  </Button>
                </NavLayout>
              );
            }}
          />
        );
      } else if (product.expirationStatus === ProductExpirationStatusType.ExpiredRedirectCatalog) {
        //TODO: messages.add_message(request, messages.INFO, REDIRECT_MESSAGE)

        return (
          <Redirect to={productCatalogPath} />
        );
      }
    }

    // redirect to real product url if slug or category or namespace is broken
    if (
      (params.namespace !== productNamespace?.slug)
      || (params.slug !== product.slug)
      || (params.category !== catalog.slug)
    ) {
      return (
        <Redirect
          to={`${productCatalogPath}/${product.id}-${product.slug}`}
        />
      );
    }

    if (product.owner.ban?.isActive) {
      //TODO: messages.add_message(request, messages.WARNING, BAN_MESSAGE)

      return (
        <Redirect
          to={generatePath(publicMemberProfile, { username: product.owner.username })}
        />
      );
    }

    if (isProductRestrictVisible) {
      if (!productDetailsData.productDetails.isOwner && !productDetailsViewerData?.viewer.isAdmin) {
        //TODO: messages.add_message(request, messages.INFO, REDIRECT_MESSAGE)

        return (
          <Redirect to={productCatalogPath} />
        );
      }

      if (productDetailsViewerData?.viewer.isAdmin) {
        //TODO: messages.add_message(request, messages.INFO, ADMIN_MESSAGE)
      }
    }
  }

  return (
    <>
      <Helmet>
        <link
          rel='preload'
          as='image'
          href={productDetailsData?.productDetails.photos[0].thumbnailUrl}
        />
      </Helmet>

      <ProductDetailsMetaContainer
        productId={productId}
      />

      <NavLayout className={s.root}>
        <ProductDetailsBreadcrumbsContainer
          className={s.breadcrumbs}
          catalog={catalogData?.catalog}
        />

        {isProductInactive && (
          <>
            {!!alertMessage && (
              <Alert className={s.soldAlert}>
                {alertMessage}
              </Alert>
            )}

            <div id='similar-products'>
              <ProductDetailsSimilarProductsContainer
                className={s.similarProducts}
                productId={productId}
              />
            </div>

            <ProductDetailsAlsoSearchContainer
              className={s.alsoSearch}
              productId={productId}
            />
          </>
        )}

        <div className={s.content}>
          <main className={s.main}>
            <ProductDetailsInfoContainer
              product={productDetailsData?.productDetails}
              catalog={catalogData?.catalog}
            />

            <ViewportLazy>
              <ProductDetailsPostsContainer className={s.posts} />
            </ViewportLazy>

            <ProductDetailsPropertiesContainer
              className={s.properties}
              product={productDetailsData?.productDetails}
              catalog={catalogData?.catalog}
              catalogSlug={params.category}
            />
          </main>

          <aside className={s.aside}>
            {productDetailsData?.productDetails.isOwner && (
              <>
                <ViewportLazy>
                  <ProductDetailsSellerSettingsContainer
                    className={s.sellerActions}
                    productId={productId}
                  />
                </ViewportLazy>
              </>
            )}

            <ViewportLazy ssr={true}>
              <ProductDetailsSellerInfoContainer product={productDetailsData?.productDetails} />

              <ProductDetailsSafePaymentHelpLink className={s.safePaymentLink} />
            </ViewportLazy>

            {!!productDetailsViewerData?.viewer && !productDetailsViewerData?.viewer.isSeller ? (
              <ViewportLazy>
                <ProductDetailsStartSellingBanner className={s.startSellingBanner} />
              </ViewportLazy>
            ) : (
              <ViewportLazy>
                <ProductDetailsAds className={s.ads} />
              </ViewportLazy>
            )}

            <ViewportLazy ssr={true}>
              <ProductDetailsOwnerProductsContainer
                className={s.ownerProducts}
                productId={productId}
                product={productDetailsData?.productDetails}
              />
            </ViewportLazy>

            {!!productDetailsViewerData?.viewer && !productDetailsViewerData?.viewer.isSeller && (
              <ViewportLazy>
                <ProductDetailsAds className={s.ads} />
              </ViewportLazy>
            )}
          </aside>

          <div className={s.additional}>
            <div className={s.socialsShareWithComplaint}>
              <ProductDetailsSocialsShareContainer
                product={productDetailsData?.productDetails}
              />

              {!productDetailsData?.productDetails.isOwner && (
                <UserComplaintContainer product={productDetailsData?.productDetails} />
              )}
            </div>

            <ViewportLazy>
              <ProductDetailsFeatureBannerContainer
                className={s.featureBanner}
                product={productDetailsData?.productDetails}
              />
            </ViewportLazy>

            <ViewportLazy>
              <ProductDetailsSellerMethodsContainer
                className={s.sellerMethods}
                productId={productId}
              />
            </ViewportLazy>

            {productDetailsData?.productDetails.isOwner && getIsProductCanBePromoted(productDetailsData.productDetails) && (
              <ViewportLazy>
                <ProductDetailsPromotionServicesContainer
                  className={s.promotions}
                  productId={productId}
                />
              </ViewportLazy>
            )}

            {!isProductInactive && (
              <ViewportLazy ssr={true}>
                <ProductDetailsAlsoSearchContainer
                  className={s.alsoSearch}
                  productId={productId}
                />
              </ViewportLazy>
            )}
            {!productDetailsData?.productDetails.isOwner && (
              <ViewportLazy>
                <ProductDetailsMessageFormContainer
                  className={s.messageForm}
                  product={productDetailsData?.productDetails}
                />
              </ViewportLazy>
            )}

            <ViewportLazy>
              <ProductDetailsReviewsContainer
                className={s.reviews}
                productId={productId}
              />
            </ViewportLazy>

            <ViewportLazy>
              <ProductDetailsViewedProducts
                className={s.viewedProducts}
                productId={productId}
              />
            </ViewportLazy>

            {!isProductInactive && (
              <div id='similar-products'>
                <ViewportLazy ssr={true}>
                  <ProductDetailsSimilarProductsContainer
                    className={s.similarProducts}
                    productId={productId}
                  />
                </ViewportLazy>
              </div>
            )}

            <ViewportLazy ssr={true}>
              <ProductDetailsTopProductsContainer
                className={s.topProducts}
                productId={productId}
              />
            </ViewportLazy>

            <ViewportLazy>
              <ProductDetailsAds className={s.ads} />
            </ViewportLazy>

            {!!params.category && (
              <ViewportLazy ssr={true}>
                <ProductDetailsTopSellersProductsContainer
                  className={s.topSellersProducts}
                  catalogSlug={params.category}
                />
              </ViewportLazy>
            )}
          </div>
        </div>

        {isClient && !isDesktop && (
          <ProductDetailsBottomBarContainer
            className={s.bottomBar}
            product={productDetailsData?.productDetails}
          />
        )}
      </NavLayout>

      {isClient && (
        <ProductDetailsAnalyticsContainer
          product={productDetailsData?.productDetails}
          catalog={catalogData?.catalog}
          withIncrViews={true}
        />
      )}
    </>
  );
};

export default ProductDetailsPage;
